// D46-D48: which borrows a repayment pays back. The panel lists the open loans of one location + coin, each with a
// checkbox and a quantity, PREFILLED from the server's proposal ("legkedvezőbb először": the highest borrow price
// first). The owner may untick or change anything; the server only checks on save. Two modes:
//   create  - the values go into the entry form's submit body (`repay_links`), reported through `onChange`;
//   assign  - an existing repay is (re)assigned: its own Save button PUTs /events/{id}/repay-links.
// Optional "Teljes lezárás" (closeMode): the principal part is the sum of the selected loans' outstanding.
// All arithmetic is exact decimal (src/lib/decimal.ts); the server stays authoritative.
import { useEffect, useMemo, useRef, useState } from 'react';
import { getLoans, getRepaySuggestion, putRepayLinks } from '../api/endpoints';
import { useApi } from '../api/hooks';
import type { LoanLine, RepaySuggestionView } from '../api/model';
import { addDecimal, compareDecimal, formatDecimal, isDecimalString } from '../lib/decimal';
import { closeMath, interestError, planLinks, principalPart, type PanelLoan, type PlanLine, type RowInput, type ServerResult } from '../lib/repayLinks';
import { formatUtc } from '../lib/time';
import { useDebounced } from '../lib/useDebounced';
import { ErrorNotice } from './Async';
import { HelpButton, HelpLabel, HelpText } from './Help';
import { PriceSourceLabel } from './PriceSource';
import { Table, type Column } from './Table';
import { Dash, signClass } from './Val';

export interface RepayPanelState {
  links: { borrow_event_id: string; quantity: string }[];
  linkedTotal: string;
  valid: boolean;
  /** Principal part minus linked (null in close mode or when the principal is unknown). */
  unlinked: string | null;
  /** Sum of the outstanding of the selected (ticked) loans. */
  selectedOutstanding: string;
  selectedCount: number;
  /** From the server proposal (location + coin level); null until it has arrived. */
  principalOutstanding: string | null;
  accrued: string | null;
  accrues: boolean | null;
  hasLoans: boolean;
  /** The proposal for the current inputs has arrived (or failed): links can be trusted. False while it is being fetched. */
  settled: boolean;
}

export interface RepayPanelProps {
  mode: 'create' | 'assign';
  locationId: string;
  locationName: string;
  asset: string;
  /** The whole repay quantity (principal + interest). */
  quantity: string;
  /** The interest part ('' = 0). Assign mode: the STORED value; the panel edits its own copy. */
  interest: string;
  /** The repay's time (UTC ISO) for the proposal's repay-minute price level. */
  occurredAt?: string | null;
  /** Assign mode: the repay being (re)assigned, so its own links do not count as taken. */
  repayEventId?: string;
  /** Create mode: the entry form's "Teljes lezárás". Assign mode has its own checkbox and ignores this. */
  closeMode?: boolean;
  onChange?: (s: RepayPanelState) => void;
  /** Assign mode: called after a successful save, with the interest part that is stored now. */
  onSaved?: (info: { interest: string; interestChanged: boolean }) => void;
  /** Create mode: put a quantity into the form. */
  onFill?: (q: string) => void;
}

const resultClass = (v: string | null): string => {
  const c = signClass(v);
  return c === 'neg' ? 'warn' : c;
};

/** Gain `pos`, loss `warn`, unknown = dash with its reason. */
export function ResultCell({ value, reason, testId }: { value: string | null; reason: string | null; testId?: string }) {
  if (value === null) return <span data-testid={testId}><Dash reason={reason} /></span>;
  return <span className={`mono ${resultClass(value)}`} data-testid={testId}>{formatDecimal(value, { dp: 2, sign: true })} USD</span>;
}

const sortLoans = (a: PanelLoan, b: PanelLoan): number => {
  if (a.price_level !== null && b.price_level !== null) {
    const c = compareDecimal(b.price_level, a.price_level);
    if (c !== 0) return c;
  } else if (a.price_level !== null) return -1;
  else if (b.price_level !== null) return 1;
  return a.occurred_at.localeCompare(b.occurred_at);
};

const ROW_ERROR: Record<'invalid' | 'exceeds_outstanding', (l: PanelLoan, asset: string) => string> = {
  invalid: () => 'Pozitív szám kell.',
  exceeds_outstanding: (l, asset) => `Legfeljebb ${formatDecimal(l.outstanding)} ${asset} lehet (a hitel kint lévő része).`,
};

export function RepayPanel(props: RepayPanelProps) {
  const { mode, locationId, locationName, asset, quantity, interest, occurredAt = null, repayEventId, closeMode: closeProp = false } = props;
  const assign = mode === 'assign';
  // Assign mode (D50): the panel owns the interest field and the "Teljes lezárás" box; create mode gets both from the entry form.
  const [interestInput, setInterestInput] = useState(interest);
  const [closeLocal, setCloseLocal] = useState(false);
  const closeMode = assign ? closeLocal : closeProp;
  const interestUsed = assign ? interestInput : interest;
  const onChangeRef = useRef(props.onChange);
  onChangeRef.current = props.onChange;

  const enabled = !!locationId && !!asset;
  // Assigning an existing repay: a loan its own links already cover counts as open again ('all' + the own link quantity added back).
  const loansState = useApi((s) => (enabled ? getLoans({ status: repayEventId ? 'all' : 'open', location_id: locationId, asset }, s) : Promise.resolve([])), [enabled, locationId, asset, repayEventId]);

  const qtyNorm = quantity.trim().replace(',', '.');
  const principal = closeMode ? null : principalPart(qtyNorm, interestUsed);
  const ownLinked = (l: LoanLine): string => l.linked_repays.filter((r) => r.repay_event_id === repayEventId).reduce((s, r) => addDecimal(s, r.quantity), '0');
  const openSum = useMemo(() => (loansState.data ?? []).reduce((s, l) => addDecimal(s, addDecimal(l.outstanding_quantity, repayEventId ? ownLinked(l) : '0')), '0'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loansState.data, repayEventId]);
  // The proposal is asked for the principal part (links are checked against it); in close mode for everything that is open.
  const wanted = closeMode ? (compareDecimal(openSum, '0') > 0 ? openSum : null) : principal;
  const debounced = useDebounced(wanted && enabled && loansState.data ? wanted : null, 300);
  const debouncedAt = useDebounced(occurredAt, 300);
  const suggKey = debounced ? [locationId, asset, debounced, debouncedAt ?? '', repayEventId ?? ''].join('|') : null;
  const sugg = useApi(
    async (s): Promise<{ key: string | null; data: RepaySuggestionView | null }> => {
      if (!suggKey || !debounced) return { key: null, data: null };
      const data = await getRepaySuggestion(
        { location_id: locationId, asset, quantity: debounced, ...(debouncedAt ? { occurred_at: debouncedAt } : {}), ...(repayEventId ? { repay_event_id: repayEventId } : {}) },
        s,
      );
      return { key: suggKey, data };
    },
    [suggKey],
  );
  const suggestion = sugg.data?.data ?? null;

  // The open loans, with the outstanding as the proposal sees it (net of every OTHER repay's links).
  const loans: PanelLoan[] = useMemo(() => {
    const byId = new Map<string, PanelLoan>();
    for (const l of loansState.data ?? []) {
      byId.set(l.event_id, {
        id: l.event_id, occurred_at: l.occurred_at, outstanding: repayEventId ? addDecimal(l.outstanding_quantity, ownLinked(l)) : l.outstanding_quantity, price_level: l.price_level, price_level_reason: l.price_level_reason,
        price_level_source: l.price_level_source, price_kind: l.price_kind,
      });
    }
    for (const i of suggestion?.items ?? []) {
      if (byId.has(i.borrow_event_id)) continue;
      byId.set(i.borrow_event_id, {
        id: i.borrow_event_id, occurred_at: i.occurred_at, outstanding: i.outstanding_quantity, price_level: i.price_level,
        price_level_reason: i.realized_result_reason, price_level_source: null, price_kind: 'coin',
      });
    }
    return [...byId.values()].filter((l) => compareDecimal(l.outstanding, '0') > 0).sort(sortLoans);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loansState.data, suggestion, repayEventId]);

  const [rows, setRows] = useState<Record<string, RowInput>>({});
  const [applied, setApplied] = useState<string | null>(null);
  // Once the OWNER touches a checkbox or a quantity the panel never re-applies a proposal by itself (D46: the owner
  // chooses the loans); a changed quantity/interest/time only re-validates. "Javaslat újra" applies it on demand.
  const [touched, setTouched] = useState(false);
  const [reapply, setReapply] = useState(0);
  const touchedRef = useRef(false);
  touchedRef.current = touched;
  const loanSig = loans.map((l) => `${l.id}:${l.outstanding}`).join(',');

  // Prefill: close mode ticks everything; otherwise the proposal of the current key (once per key).
  useEffect(() => { setTouched(false); }, [closeMode]); // switching the mode starts from that mode's default selection
  useEffect(() => {
    if (closeMode && !touchedRef.current) {
      setRows(Object.fromEntries(loans.map((l) => [l.id, { on: true, qty: l.outstanding }])));
      setApplied(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeMode, loanSig, touched]);
  useEffect(() => {
    if (closeMode) return;
    if (touchedRef.current) {
      // the owner's selection stays; only remember that this key was seen so the form can settle
      if (suggKey && sugg.data && sugg.data.key === suggKey && applied !== suggKey) setApplied(suggKey);
      return;
    }
    if (!suggKey) {
      if (applied !== null || wanted === null) { setRows({}); setApplied(null); }
      return;
    }
    if (sugg.data && sugg.data.key === suggKey && sugg.data.data && applied !== suggKey) {
      setRows(Object.fromEntries(sugg.data.data.items.map((i) => [i.borrow_event_id, { on: compareDecimal(i.proposed_quantity, '0') > 0, qty: compareDecimal(i.proposed_quantity, '0') > 0 ? i.proposed_quantity : '' }])));
      setApplied(suggKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeMode, suggKey, sugg.data, reapply, touched]);

  const server: Record<string, ServerResult> = useMemo(
    () => Object.fromEntries((suggestion?.items ?? []).map((i) => [i.borrow_event_id, { proposed: i.proposed_quantity, value: i.realized_result_usd, reason: i.realized_result_reason }])),
    [suggestion],
  );
  const repayLevel = { level: suggestion?.repay_price_level ?? null, reason: suggestion?.repay_price_level_reason ?? null };
  const plan = planLinks(loans, rows, principal, repayLevel, server);
  const arrived = closeMode ? sugg.data?.key === suggKey : applied === suggKey;
  const settled = wanted === null || (debounced === wanted && debouncedAt === occurredAt && (arrived || !!sugg.error));
  const selected = plan.lines.filter((l) => l.on);
  const selectedOutstanding = selected.reduce((s, l) => addDecimal(s, l.loan.outstanding), '0');

  const state: RepayPanelState = {
    links: plan.links, linkedTotal: plan.linkedTotal, valid: plan.valid, unlinked: closeMode ? null : plan.unlinked, selectedOutstanding, selectedCount: selected.length,
    principalOutstanding: suggestion?.principal_outstanding_quantity ?? null, accrued: suggestion?.accrued_interest_quantity ?? null,
    accrues: suggestion?.location_accrues_interest ?? null, hasLoans: loans.length > 0, settled,
  };
  const stateKey = JSON.stringify(state);
  useEffect(() => {
    onChangeRef.current?.(state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateKey]);

  const setRow = (id: string, patch: Partial<RowInput>) => {
    setTouched(true);
    setRows((r) => ({ ...r, [id]: { on: false, qty: '', ...r[id], ...patch } }));
  };
  const toggle = (line: PlanLine, on: boolean) => {
    if (!on) return setRow(line.loan.id, { on: false, qty: '' });
    const free = principal === null ? line.loan.outstanding : plan.over ? '0' : addDecimal('0', plan.unlinked ?? '0');
    const take = closeMode || compareDecimal(free, '0') <= 0 || compareDecimal(free, line.loan.outstanding) > 0 ? line.loan.outstanding : free;
    return setRow(line.loan.id, { on: true, qty: take });
  };

  const reproposal = () => { setTouched(false); setApplied(null); setReapply((n) => n + 1); };

  // Assign mode: the interest part after the save. Close mode: the rest of the quantity over the ticked loans (read-only);
  // otherwise the typed value. Sent only when it differs from the stored one (always in close mode).
  const closeCalc = assign && closeMode ? closeMath(qtyNorm, selectedOutstanding, selected.length, null) : null;
  const interestErr = assign && !closeMode ? interestError(qtyNorm, interestInput) : null;
  const norm = (v: string): string => (v.trim() === '' ? '0' : v.trim().replace(',', '.'));
  const newInterest: string | null = !assign ? null : closeMode ? (closeCalc?.interest ?? null) : interestErr === null ? norm(interestInput) : null;
  const storedInterest = norm(interest);
  const interestChanged = newInterest !== null && isDecimalString(newInterest) && isDecimalString(storedInterest) && compareDecimal(newInterest, storedInterest) !== 0;
  const interestOk = !assign || (closeMode ? closeCalc !== null && closeCalc.error === null : interestErr === null);
  const exceedsOpen = assign && !closeMode && compareDecimal(openSum, '0') > 0 && isDecimalString(qtyNorm) && compareDecimal(principal ?? qtyNorm, openSum) > 0;

  const [helpOpen, setHelpOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<Error | null>(null);
  const save = async () => {
    if (!repayEventId) return;
    setSaving(true);
    setSaveError(null);
    try {
      const opts: { interest_quantity?: string; close?: boolean } = {};
      if (closeMode) {
        opts.close = true;
        if (newInterest !== null) opts.interest_quantity = newInterest;
      } else if (interestChanged && newInterest !== null) opts.interest_quantity = newInterest;
      await putRepayLinks(repayEventId, plan.links, opts);
      props.onSaved?.({ interest: newInterest ?? storedInterest, interestChanged });
    } catch (e) {
      setSaveError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setSaving(false);
    }
  };

  if (loansState.data && loans.length === 0 && !suggestion) {
    return (
      <div className="box" data-testid="repay-panel">
        <b>Visszafizetendő hitelek</b>
        <div className="notice warn small" role="status">
          Nincs nyitott {asset} hitel a(z) „{locationName}” helyszínen. Ellenőrizd a helyszínt és a coint; a hitelfelvételt előbb rögzíteni kell.
        </div>
      </div>
    );
  }

  const cols: Column<PlanLine>[] = [
    {
      key: 'pick', label: 'Kijelölés', srOnly: true,
      render: (l) => (
        <input
          type="checkbox" checked={l.on} data-testid={`repay-check-${l.loan.id}`}
          aria-label={`Hitel kijelölése: ${formatUtc(l.loan.occurred_at)} UTC, kint ${formatDecimal(l.loan.outstanding)} ${asset}`}
          onChange={(e) => toggle(l, e.target.checked)}
        />
      ),
    },
    { key: 'at', label: 'Felvéve (UTC)', sortValue: (l) => l.loan.occurred_at, render: (l) => <span className="mono">{formatUtc(l.loan.occurred_at)}</span> },
    { key: 'out', label: 'Kint lévő', numeric: true, sortValue: (l) => l.loan.outstanding, render: (l) => <span className="mono">{formatDecimal(l.loan.outstanding)} {asset}</span> },
    {
      key: 'level', label: 'Felvételi ár', numeric: true, sortValue: (l) => l.loan.price_level,
      render: (l) => (
        <span className="mono">
          {l.loan.price_level === null ? <Dash reason={l.loan.price_level_reason} /> : formatDecimal(l.loan.price_level, { dp: 2 })}
          <PriceSourceLabel source={l.loan.price_level_source} />
        </span>
      ),
    },
    {
      key: 'qty', label: 'Visszafizetés', numeric: true, ariaLabel: 'visszafizetett mennyiség',
      render: (l) => (
        <span className="stack" style={{ gap: 2, alignItems: 'flex-end' }}>
          <input
            className="input mono" style={{ width: 130, minHeight: 34, textAlign: 'right' }} inputMode="decimal" value={l.on ? l.qty : ''} disabled={closeMode}
            data-testid={`repay-qty-${l.loan.id}`} aria-label={`Visszafizetett mennyiség: ${formatUtc(l.loan.occurred_at)} UTC`}
            aria-invalid={l.error !== null}
            onChange={(e) => setRow(l.loan.id, { qty: e.target.value, on: e.target.value.trim() !== '' ? true : (rows[l.loan.id]?.on ?? false) })}
          />
          {l.error ? <span className="neg small" role="alert" data-testid={`repay-row-error-${l.loan.id}`}>{ROW_ERROR[l.error](l.loan, asset)}</span> : null}
        </span>
      ),
    },
    {
      key: 'take', label: 'Kint: előtte → utána', numeric: true,
      render: (l) => (
        <span className="mono" data-testid="repay-take">
          {l.on && l.after !== null ? `${formatDecimal(l.loan.outstanding)} → ${formatDecimal(l.after)}` : <span className="muted">—</span>}
        </span>
      ),
    },
    {
      key: 'res', label: 'Realizált eredmény', numeric: true, help: 'realizedResult', ariaLabel: 'realizált eredmény',
      render: (l) => (l.result ? <ResultCell value={l.result.value} reason={l.result.reason} testId={`repay-result-${l.loan.id}`} /> : <span className="muted">—</span>),
    },
  ];

  return (
    <div className="box" data-testid="repay-panel" data-mode={mode}>
      <div className="card-head">
        <span className="row-wrap" style={{ alignItems: 'center' }}>
          <b>{mode === 'assign' ? 'Hozzárendelés a hitelekhez' : 'Visszafizetendő hitelek'}</b>
          <HelpButton label="hitelhez rendelés" open={helpOpen} onToggle={() => setHelpOpen((o) => !o)} />
        </span>
        {mode === 'create' && !closeMode && props.onFill && compareDecimal(openSum, '0') > 0 ? (
          <button
            type="button" className="btn small"
            onClick={() => props.onFill?.(addDecimal(openSum, isDecimalString(interest.trim().replace(',', '.')) ? interest.trim().replace(',', '.') : '0'))}
          >
            Teljes visszafizetés ({formatDecimal(openSum)} {asset})
          </button>
        ) : null}
      </div>
      {helpOpen ? <HelpText helpKey="repayLinks" /> : null}
      {assign ? (
        <div className="stack" style={{ gap: 8 }} data-testid="repay-assign-extras">
          <div className="field">
            <HelpLabel helpKey="interestPart" label="ebből kamat" className="flabel">Ebből kamat</HelpLabel>
            <input
              id="assign-interest" data-testid="assign-interest-input" aria-label="Ebből kamat" className="input mono" inputMode="decimal" placeholder="0"
              aria-invalid={interestErr !== null} disabled={closeMode}
              value={closeMode ? (closeCalc?.interest ?? '') : interestInput}
              onChange={(e) => setInterestInput(e.target.value)} onFocus={(e) => e.currentTarget.select()}
            />
            {closeMode ? (
              closeCalc?.interest != null ? (
                <span className="hint" data-testid="assign-close-interest">Kamat: <b className="mono">{formatDecimal(closeCalc.interest)} {asset}</b> (a visszafizetés mennyisége − a kijelölt hitelek kint lévő része)</span>
              ) : null
            ) : interestErr ? (
              <span className="hint neg" role="alert" data-testid="interest-error">
                {interestErr === 'invalid' ? 'A kamat nulla vagy pozitív decimális szám lehet.' : 'A kamat nem lehet több, mint a visszafizetett mennyiség.'}
              </span>
            ) : (
              <span className="hint" data-testid="interest-hint">
                {principal !== null ? `Tőke-rész: ${formatDecimal(principal)} ${asset}. A kamat-rész sosem vár hozzárendelésre.` : 'A visszafizetett mennyiségből a kamat; üresen 0.'}
              </span>
            )}
          </div>
          <label className="check">
            <input type="checkbox" data-testid="assign-close-toggle" checked={closeMode} onChange={(e) => setCloseLocal(e.target.checked)} />
            <span>Teljes lezárás (a többlet kamat)</span>
          </label>
          {exceedsOpen ? (
            <div className="notice info small" role="status" data-testid="assign-exceeds-open">
              A visszafizetés mennyisége ({formatDecimal(qtyNorm)} {asset}) több, mint a nyitott hitelek összesen ({formatDecimal(openSum)} {asset}). Ha a különbség kamat, használd a „Teljes lezárás” jelölést: a többlet kamatként rögzül.
            </div>
          ) : null}
          {closeCalc?.error === 'qty_below_principal' ? (
            <div className="notice err small" role="alert" data-testid="close-error">
              A kijelölt hitelek kint lévő része ({formatDecimal(selectedOutstanding)} {asset}) több, mint a visszafizetés mennyisége ({formatDecimal(qtyNorm)} {asset}): a kamat negatív lenne. Vegyél ki hitelt a kijelölésből.
            </div>
          ) : null}
          {closeCalc?.error === 'no_loan_selected' ? <div className="notice err small" role="alert" data-testid="close-error">Jelölj ki legalább egy lezárandó hitelt.</div> : null}
        </div>
      ) : null}
      {closeMode ? (
        <div className="muted small" data-testid="repay-close-note">
          Teljes lezárás: a kijelölt hitelek teljes kint lévő része a tőke; a beírt mennyiség többlete a kamat. Hitelt a pipa kivételével zárhatsz ki.
        </div>
      ) : (
        <div className="muted small row-wrap" style={{ alignItems: 'center' }} data-testid="repay-proposal-note">
          <span>
            {touched
              ? 'A kijelölést te állítottad be: a mennyiség, a kamat vagy az idő módosítása már nem írja felül, csak újra ellenőrzi.'
              : 'Javaslat, legkedvezőbb először: a legmagasabb felvételi árú hitelt fizeti vissza először. A pipák és a mennyiségek átírhatók; a szerver mentéskor ellenőrzi őket.'}
          </span>
          {touched ? <button type="button" className="btn small" data-testid="repay-reapply" onClick={reproposal}>Javaslat újra</button> : null}
        </div>
      )}
      {sugg.error ? <ErrorNotice error={sugg.error} /> : null}
      {loansState.error ? <ErrorNotice error={loansState.error} /> : null}
      <Table columns={cols} rows={plan.lines} rowKey={(l) => l.loan.id} dense label="Visszafizetendő hitelek" empty="Nincs nyitott hitel." />
      <div className="row-wrap" style={{ justifyContent: 'space-between' }}>
        <span className="small" data-testid="repay-linked">
          Hitelekhez rendelve: <b className="mono">{formatDecimal(plan.linkedTotal)} {asset}</b>
          {!closeMode && principal !== null ? <> a {formatDecimal(principal)} {asset} tőke-részből</> : null}
        </span>
        <span className="small">Összes realizált eredmény: <ResultCell value={plan.total.value} reason={plan.total.reason} testId="repay-result-total" /></span>
      </div>
      {plan.over && principal !== null ? (
        <div className="notice warn small" role="alert" data-testid="repay-over">
          A hitelekhez rendelt {formatDecimal(plan.linkedTotal)} {asset} több, mint a visszafizetés tőke-része ({formatDecimal(principal)} {asset}). Csökkentsd a mennyiségeket.
        </div>
      ) : null}
      {!closeMode && !plan.over && plan.unlinked !== null && compareDecimal(plan.unlinked, '0') > 0 ? (
        <div className="notice info small" role="status" data-testid="repay-unassigned-note">
          A tőke-részből {formatDecimal(plan.unlinked)} {asset} nincs hitelhez rendelve: ez „Hozzárendelésre vár” jelzéssel marad, és később a Hitelek oldalon rendelheted hozzá. A kamat-rész nem vár hozzárendelésre.
        </div>
      ) : null}
      {mode === 'assign' ? (
        <>
          {saveError ? <ErrorNotice error={saveError} /> : null}
          <div className="row-wrap" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn primary" data-testid="repay-assign-save" disabled={saving || !plan.valid || plan.links.length === 0 || !interestOk} onClick={() => void save()}>
              {saving ? 'Mentés…' : 'Hozzárendelés mentése'}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}
